import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Mail, AlertOctagon, Lock, Newspaper, Scale } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import { useJsonLd, ORG_JSONLD, buildBreadcrumb } from '../utils/jsonLd';
import PageSeoTag from '../components/PageSeoTag';

const PILLARS = [
  {
    icon: Newspaper,
    title: 'Unfiltered Reporting',
    body:
      "We cover the stories mainstream media sanitises, downplays, or ignores. No editor on Collins Street is going to tell us a piece is 'too raw'.",
  },
  {
    icon: Shield,
    title: 'Source Protection',
    body:
      "Tips arrive through encrypted, IP-free channels. We've never named a source and we never will. If a court order ever came for them, we burn the records first.",
  },
  {
    icon: Scale,
    title: 'Independent, No Strings',
    body:
      "No mainstream masthead pulling the strings, no political alignment. We follow the story wherever it goes - even when it's uncomfortable.",
  },
];

const STANDARDS = [
  "We verify every claim with at least two independent sources before publication.",
  "We don't pay for tips. Ever. Money corrupts intel and we'd rather miss a story than buy a fake one.",
  "We correct mistakes publicly and quickly. Corrections sit at the bottom of the original article forever.",
  "We don't publish identifying details about minors, victims of sexual crime, or anyone in genuine ongoing danger.",
  "We hold suppression orders in respect - but we'll fight to overturn ones that exist only to protect the powerful.",
  "When sources contradict each other, we say so on the record.",
];

const Section = ({ icon: Icon, title, children, id }) => (
  <section id={id} className="mb-12 scroll-mt-24" data-testid={`about-section-${id}`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-5 w-5 text-red-500" />
      <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider">{title}</h2>
    </div>
    {children}
  </section>
);

const About = () => {
  useJsonLd(ORG_JSONLD, 'about-org');
  useJsonLd(buildBreadcrumb([
    { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
    { name: 'About', url: 'https://www.outlawmedia.com.au/about' },
  ]), 'about-breadcrumb');

  return (
    <div className="min-h-screen bg-black text-white" data-testid="about-page">
      <TopNav />
      <PageSeoTag pageKey="about" />

      <header className="relative pt-8 pb-8 px-4 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="about-back-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-red-400 transition-colors mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 mb-1">
            About
          </p>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
            Independent Crime Journalism From <span className="text-red-500">Australia's</span> Underworld
          </h1>
          <p className="mt-3 text-sm sm:text-base text-gray-400 max-w-2xl">
            Outlaw Media is a small but passionate team headed by <strong className="text-white">Ryan Naumenko</strong>,
            covering gangland conflicts, organised crime, and the bent edges of Australian public life. No editor on
            Collins Street. No filter.
          </p>
        </div>
      </header>

      <main className="px-4 py-8">
        <div className="max-w-3xl mx-auto">

          <Section id="who" icon={FileText} title="Who We Are">
            <div className="space-y-3 text-sm sm:text-base text-gray-300 leading-relaxed">
              <p>
                Outlaw Media is a small but passionate team headed by{' '}
                <strong className="text-white">Ryan Naumenko</strong> - a Melbourne-based independent
                journalist with relationships built over years inside Australia's criminal landscape.
                Headquartered in Dubai for operational reasons; reporting from Melbourne, Sydney,
                Brisbane, Perth, and wherever the story goes.
              </p>
              <p>
                We publish written investigations right here on outlawmedia.com.au, daily underworld
                updates on Instagram, long-form interviews on YouTube, and explicit gangland footage
                on this site.
              </p>
            </div>
          </Section>

          <Section id="pillars" icon={Shield} title="What We Stand For">
            <div className="grid sm:grid-cols-3 gap-3">
              {PILLARS.map(({ icon: I, title, body }) => (
                <div
                  key={title}
                  className="rounded-xl bg-zinc-900/60 border border-red-900/30 p-4 hover:border-red-500/50 transition-colors"
                >
                  <I className="h-5 w-5 text-red-500 mb-2" />
                  <h3 className="text-sm font-black uppercase tracking-wider mb-1.5">{title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="standards" icon={Scale} title="Editorial Standards">
            <ul className="space-y-2.5">
              {STANDARDS.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-gray-300 leading-relaxed">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600/15 border border-red-500/40 text-red-400 text-[10px] font-black">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section id="anti" icon={AlertOctagon} title="What We Won't Do">
            <div className="rounded-xl bg-red-950/30 border border-red-500/40 p-4 sm:p-5">
              <ul className="space-y-2 text-sm text-gray-200 leading-relaxed">
                <li>• Pay sources for information</li>
                <li>• Endanger anyone's life or family for a click</li>
                <li>• Take payment to suppress, soften, or shape coverage</li>
                <li>• Reveal a source's identity - under any pressure, ever</li>
                <li>• Run sponsored editorial dressed up as reporting</li>
              </ul>
            </div>
          </Section>

          <Section id="contact" icon={Mail} title="Contact">
            <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 p-4 sm:p-5 space-y-3 text-sm">
              <div className="flex items-start gap-2.5">
                <Lock className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-white font-bold">Tips (anonymous)</p>
                  <Link to="/tip" data-testid="about-contact-tip" className="text-red-400 hover:text-red-300 underline">outlawmedia.com.au/tip</Link>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-white font-bold">Press / legal / business</p>
                  <a
                    href="mailto:ryan@outlawmedia.com.au"
                    data-testid="about-contact-email"
                    className="text-red-400 hover:text-red-300 underline"
                  >
                    ryan@outlawmedia.com.au
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <FileText className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-white font-bold">Other outlets / journalists</p>
                  <Link to="/press" data-testid="about-contact-press" className="text-red-400 hover:text-red-300 underline">Press kit & logos</Link>
                </div>
              </div>
            </div>
          </Section>

          <p className="text-[10px] text-gray-600 tracking-widest uppercase text-center mt-8">
            Outlaw Media · Independent · Source-Protected · Since 2024
          </p>
        </div>
      </main>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default About;
